import { FC } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../hooks/useStore';
import { NoteOverlay } from './overlays';
import { Table, TableProps } from '../../../lib/components/compound/table';
import { ReceiptsRowViewModel } from './row/viewModel';
import { ReceiptsViewModel } from '../../../viewModels/receipts';
import { ReceiptsTableViewModel } from './viewModel';
import { ReceiptActionDialogNew } from '../dialogs/action/component';
import { ButtonConfig } from '../../../lib/components/compound/tables/presentation/table/roofs/shared';
import { GridSelectionModel } from '@mui/x-data-grid-pro';
import {
  ApprovedIcon,
  DeleteIcon,
  StatusRejectedIcon,
  StatusSuccessIcon
} from '../../../lib/icons';
import { useTranslation } from 'react-i18next';
import { ReceiptAction } from '../dialogs/action/viewModel';

export interface ReceiptsTableProps extends TableProps<
  ReceiptsViewModel,
  ReceiptsRowViewModel,
  ReceiptsTableViewModel
> {
  openActionDialog: (action: ReceiptAction) => void;
}

export const ReceiptsTable: FC<ReceiptsTableProps> = observer(
  ({ viewModel, openActionDialog, ...props }) => {
    const { receiptStore } = useStore();
    const { t } = useTranslation();

    const handleApprove = async (ids: string[] | string) => {
      if (Array.isArray(ids)) {
        openActionDialog('approve');
      } else {
        await receiptStore.approve(ids);
      }
    };

    const actionButtonConfig: ButtonConfig[] = [
      {
        onClick: async (selectedEntries: GridSelectionModel) => {
          await handleApprove(selectedEntries.map((id) => id as string));
        },
        renderIcon: () => <ApprovedIcon height={26} width={26} color="success.main" />,
        tooltip: t('common:APPROVE')
      },
      {
        onClick: () => openActionDialog('settle'),
        renderIcon: () => <StatusSuccessIcon height={26} width={26} color="success.main" />,
        tooltip: t('common:SETTLE')
      },
      {
        onClick: () => openActionDialog('reject'),
        renderIcon: () => <StatusRejectedIcon height={28} width={28} color="error.main" />,
        tooltip: t('common:REJECT')
      },
      {
        onClick: () => openActionDialog('delete'),
        renderIcon: () => <DeleteIcon color="error.main" />,
        tooltip: t('common:DELETE')
      }
    ];
    return (
      <>
        <Table viewModel={viewModel} {...props} actionButtonConfig={actionButtonConfig} />
        <NoteOverlay viewModel={viewModel.noteOverlay} sx={{ marginY: '8px' }} />
        <ReceiptActionDialogNew viewModel={viewModel.actionDialog} />
      </>
    );
  }
);
